<script setup lang="ts">
  import { ref, watch, onMounted } from 'vue'
  import { useForm } from 'vee-validate'
  import { debounce, throttle } from '@/lib/timing'
  import { useSavingIndicatorStore } from '@/store/savingIndicatorStore'

  const autoSavingIndicatorStore = useSavingIndicatorStore()

  const props = defineProps({
    debounceDelay: {
      type: Number,
      default: 1000
    },
    throttleInterval: {
      type: Number,
      default: 1000
    }
  })

  const { values, meta, validate } = useForm()
  const emit = defineEmits(['save'])

  const touchedFields = ref<Record<string, boolean>>({})

  const createSnapshot = (obj: any): any => {
    if (obj === null || typeof obj !== 'object') {
      return obj
    }
    if (Array.isArray(obj)) {
      return obj.map(createSnapshot)
    }
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, createSnapshot(value)])
    )
  }

  const debouncedSave = debounce((values: any) => {
    autoSavingIndicatorStore.indicateSaving()
    emit('save', values)
  }, props.debounceDelay)

  const throttledSave = throttle((values: any) => {
    debouncedSave(values)
  }, props.throttleInterval)

  watch(
    values,
    async (oldValues, newValues) => {
      const resp = await validate()
      if (resp.valid) {
        await throttledSave(newValues)
      }
    },
    { deep: true }
  )

  const handleFieldTouch = (fieldName: string) => {
    touchedFields.value[fieldName] = true
  }

  defineExpose({
    handleFieldTouch
  })
</script>

<template>
  <div>
    <slot />
  </div>
</template>
